import { ErrorBoundary } from '@sentry/nextjs';
import { Hydrate, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import Cookies from 'js-cookie';
import { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { appWithTranslation, useTranslation } from 'next-i18next';
import { useEffect } from 'react';
import { initGTM, initMixpanel } from 'utils/analytics';
import { detectUserGeoInfo } from 'utils/general/languageDetection';

import '@/styles/nprogress.css';
import '@/styles/globals.css';

import { queryClient } from '@/_app-confiq';
import routes from '@/shared/constant/routes';
import ErrorFallback from '@/shared/hocs/ErrorFallback';
import { AuthProvider, ProtectedRoute } from '@/shared/hooks/context/auth';
import { GenericContextProvider } from '@/shared/hooks/context/createGenericContext';

const DismissableToast = dynamic(
  () => import('@/shared/components/atoms').then((mod) => mod.DismissableToast),
  {
    ssr: false,
  }
);
const RQIsFetchingLoader = dynamic(
  () =>
    import('@/shared/components/atoms').then((mod) => mod.RQIsFetchingLoader),
  {
    ssr: false,
  }
);

// import { ModalProvider } from '@/shared/hooks/context/modalContext';
const ModalProvider = dynamic(
  () =>
    import('@/shared/hooks/context/modalContext').then(
      (mod) => mod.ModalProvider
    ),
  {
    ssr: false,
  }
);

function MyApp({ Component, pageProps }: AppProps) {
  const { i18n } = useTranslation();
  const { push, asPath, locale } = useRouter();
  /*
   * Make sure to only use it when necessary as this function will run on every page
   * thats why we are using analytics in here to be intialized in every page to collect data
   *
   **/

  useEffect(() => {
    const initializeLanguage = async () => {
      // Check if language preference is already set
      const savedLang = Cookies.get('lang');

      if (!savedLang) {
        // Detect based on user's location
        const detected = await detectUserGeoInfo();
        const detectedLang = detected.language;
        // Set the cookie with the detected language
        Cookies.set('lang', detectedLang, {
          path: '/',
          sameSite: 'Strict',
          expires: new Date('2099-12-31'),
        });

        // Change the locale in the URL if it's different from the current one
        if (locale !== detectedLang) {
          // Change the locale in i18n
          i18n.changeLanguage(detectedLang);
          push(asPath, undefined, { locale: detectedLang });
        }
      } else i18n.changeLanguage(savedLang);
    };
    const initializeCountryCode = async () => {
      // Check if country code is already set
      const savedCountryCode = Cookies.get('defaultCountryCode');

      if (!savedCountryCode) {
        // Detect based on user's location
        const detectedCountryCode = (await detectUserGeoInfo()).countryCode;

        // Set the cookie with the detected country code
        Cookies.set('defaultCountryCode', detectedCountryCode, {
          path: '/',
          sameSite: 'Strict',
          expires: new Date('2099-12-31'),
        });
      }
    };
    const elem = document.querySelector('vercel-live-feedback');
    if (elem) {
      elem.remove();
    }

    initializeCountryCode();
    initializeLanguage();
    initGTM();
    initMixpanel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ErrorBoundary fallback={ErrorFallback}>
      <QueryClientProvider client={queryClient}>
        {/* for prefetching in ssr */}
        <Hydrate state={pageProps?.dehydratedState}>
          {/* loader for background fetch of react qurery */}
          <RQIsFetchingLoader />
          {/* toast to indicate  api request result */}
          <AuthProvider afterLogoutTo={routes.login}>
            <DismissableToast />
            {/* contain  asset  provider */}
            <GenericContextProvider>
              <ProtectedRoute>
                <main
                // className={`${cairo_font.variable} ${roboto_font.variable}`}
                // className={`${cairo_font.variable} font-Cairo`}
                >
                  <Head>
                    <meta
                      name='viewport'
                      content='width=device-width, initial-scale=1'
                    />
                    <script
                      type='text/javascript'
                      id='hs-script-loader'
                      async
                      defer
                      src='//js-eu1.hs-scripts.com/27019639.js'
                    ></script>
                  </Head>
                  <ModalProvider>
                    <Component {...pageProps} />
                  </ModalProvider>
                </main>
              </ProtectedRoute>
            </GenericContextProvider>
          </AuthProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </Hydrate>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
export default appWithTranslation(MyApp);
